import { CheckoutConfig, PhaseDetail } from '@share/interfaces';
import {
  ClassAttributes,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { ModalName, PaymentGatewayName } from '@share/enums';
import { market } from '@state/market';
import { payment as pay } from '@state/private';
import { showPercentage, toggleBootstrapModal } from '@util/helper';
import { toast } from 'react-toastify';
import { useAPI, useCurrency, useHelper } from 'src/hooks';
import { useDebouncedCallback } from 'use-debounce';
import { useForm } from 'react-hook-form';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { web3 } from '@state/web3';
import Agreement from './Agreement';
import DotLoading from '@com/utils/DotLoading';
import ErrorMsg from '@com/utils/ErrorMsg';
import GkashPaymentBtn from '@com/buttons/GkashPaymentBtn';
import PaymentRow from '@com/utils/PaymentRow';
import styled from '@emotion/styled';

interface Fields {
  quantity: number;
}

interface Params extends ClassAttributes<HTMLDivElement> {
  launchpadAddress: string;
  launchpadId: string;
  phase: PhaseDetail;
}

const SelectBox = styled.select`
  text-align-last: center; // For IOS
`;

const MintModal = forwardRef(
  ({ launchpadId, phase, launchpadAddress }: Params, ref) => {
    /* Define state / hook */
    const [isShow, setIsShow] = useState<boolean>();
    const [isChecked, setIsChecked] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [quantity, setQuantity] = useState(1);
    const [balance, setBalance] = useState<string | null>(null);
    const { getAddress } = web3.useContainer();
    const { mint, isSigned, payment, getBalance } = market.useContainer();
    const { setPaymentModalData } = pay.useContainer();

    const mintModal = useRef<HTMLDivElement>(null);
    const { isFiatCurrency, getMinimumUnit } = useCurrency();
    const { t } = useTranslation();
    const { displayCurrency } = useHelper();
    const { getMintStatus, processGkashMint } = useAPI();
    const phaseId = phase?.uuid;
    const { data, isFetching } = useQuery(
      [getMintStatus.name, phaseId],
      () => getMintStatus(launchpadId, phaseId, getAddress()),
      {
        enabled: isShow === true, // Prevent undefined also execute the query
        retry: false,
        onError() {
          /**
           * FIXME: If not adding this delay, the modal will not toggle when
           * 'Disabled Cache' is not enable in devtools...
           * Also for firefox only ~150ms need, but for chrome has to ~350ms
           * so use 500ms for safe
           */
          setTimeout(() => setIsShow(false), 500);
        },
      }
    );
    const {
      register,
      formState: { errors },
      handleSubmit,
      getValues,
      trigger,
    } = useForm<Fields>();

    /* Custom Data Object for render or other usages */
    const paymentMethod = phase?.payment_method;

    // TODO: Able to update via 'check' API if value change
    const currencyData = paymentMethod?.currency;
    const subtotalFee = Number(phase?.config?.price) * quantity;

    const transactionFee =
      Number(data?.gas?.base || 0) +
      Number(data?.gas?.take || 0) +
      Number(data?.gas?.increment || 0) * (quantity - 1);

    // TODO: Based on creator preference
    const handlingFee = 0;
    // const handlingFee =
    //   ((subtotalFee + transactionFee) *
    //     Number(data?.service?.handling_fee || 0)) /
    //   100;

    const totalPrice = Number(
      (subtotalFee + transactionFee + handlingFee).toFixed(
        // Here used a hacky way to get the total decimal place have to display
        String(getMinimumUnit(currencyData?.id)).split('0').length - 1
      )
    );

    /* render control function / logic */
    const toggleModal = useDebouncedCallback(
      () => {
        if (!isLoading) setIsShow(!isShow);
      },
      600,
      { leading: true, trailing: true }
    );

    const isAllowToPay = async () => {
      if (!isChecked) {
        toast.info(t('toastMessage.checkTerms'));
        return false;
      }

      const isLoginDoubleCheck = await payment();
      if (!isLoginDoubleCheck) return false;
      if (!data) return false;

      return true;
    };

    const handleCryptoMint = useDebouncedCallback(
      async (formData: Fields) => {
        if (!data) {
          toast.info(t('toastMessage.unexceptedError'));
          return;
        }

        const isLoginDoubleCheck = await payment();
        if (!isLoginDoubleCheck) return;

        setIsLoading(true);
        const isTxSuccess = await mint(
          launchpadAddress,
          data.price,
          formData.quantity,
          data.encodedData,
          data.sign,
          data.transferData,
          currencyData?.id
        );
        setIsLoading(false);

        if (!isTxSuccess) return;
        setIsShow(!isShow); // Don't use toggleModal here because isLoading state is not update
      },
      500,
      { leading: true, trailing: false }
    );

    // No debounced require (do it inside GkashPaymentBtn)
    const handleGkashFiatMint = async () => {
      if (!(await isAllowToPay())) return null;

      const values = getValues();
      const isValid = await trigger();

      if (isValid) {
        return processGkashMint({
          uuid: launchpadId,
          amount: values.quantity,
          phase: phaseId,
          totalPrice,
          walletAddress: getAddress(),
        });
      }
      return null;
    };

    const handleCheckoutFiatMint = useDebouncedCallback(
      async (formData: Fields) => {
        if (!(await isAllowToPay())) return;
        setPaymentModalData({
          uuid: launchpadId,
          amount: formData.quantity,
          totalPrice,
          phaseId,
          config: paymentMethod?.payment_gateway?.config as CheckoutConfig,
        });
        toggleModal();
        toggleBootstrapModal(ModalName.SELECT_PAYMENT);
      },
      500,
      { leading: true, trailing: false }
    );

    /* Data Change Listeners - useEffect/useMemo/useCallback */
    useEffect(() => {
      (async () => {
        if (currencyData?.id && !isFiatCurrency(currencyData.id)) {
          setBalance(await getBalance(currencyData.id));
        }
      })();
    }, [data]);

    useEffect(() => {
      if (isShow != null) toggleBootstrapModal(`mint-modal-${phaseId}`);
    }, [isShow]);

    useImperativeHandle(ref, () => ({ toggleModal }));

    /* Styling */

    /* Render */
    return (
      <div
        ref={mintModal}
        aria-hidden
        className="modal fade popup"
        id={`mint-modal-${phaseId}`}
        role="dialog"
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div
            className="modal-content"
            // Prevent clicking payment button when loading data
            style={{ pointerEvents: isFetching ? 'none' : 'auto' }}
          >
            <button
              className="close"
              type="button"
              onClick={() => {
                toggleModal();
              }}
            >
              <span aria-hidden>&times;</span>
            </button>
            <div className="modal-body space-y-20 pd-40">
              <h3>{t('modal.mintNFT')}</h3>
              <hr />
              {isLoading ? (
                <div>
                  <DotLoading />
                  <div className="row mt-4">
                    <div
                      className="col-12 text-center mt-5"
                      style={{ fontSize: '2.5em' }}
                    >
                      {isSigned
                        ? t('modal.waitContract')
                        : t('modal.signContract')}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {balance && (
                    <div className="d-flex justify-content-between align-items-center mb-5">
                      <p>{t('modal.balanceInWallet')}</p>
                      <div className="text-right">
                        <div className="price color-popup">
                          {displayCurrency(balance, currencyData?.name)}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="d-flex justify-content-between align-items-center mb-5">
                    <p>{t('modal.mintQuantity')}</p>
                    <div className="col-5 pr-0">
                      <SelectBox
                        className="p-2 w-100 text-center bg-transparent rounded-pill"
                        disabled={!data}
                        id="quantity"
                        {...register('quantity', {
                          required: true,
                          onChange: (e) => setQuantity(Number(e.target.value)),
                          value: 1,
                        })}
                      >
                        {Array.from(Array(data?.maxMintPerTx || 1).keys())?.map(
                          (index) => (
                            <option key={index} value={index + 1}>
                              {index + 1}
                            </option>
                          )
                        )}
                      </SelectBox>
                      <ErrorMsg
                        errors={errors}
                        msg={t('modal.chooseQuantity')}
                        name="quantity"
                      />
                    </div>
                  </div>
                  {isFiatCurrency(currencyData?.id) && (
                    <>
                      <PaymentRow
                        currency={currencyData?.name}
                        price={subtotalFee}
                        title={t('modal.subtotal')}
                      />
                      <PaymentRow
                        currency={currencyData?.name}
                        price={transactionFee}
                        title={t('modal.transactionFee')}
                      />
                      <PaymentRow
                        currency={currencyData?.name}
                        price={handlingFee}
                        title={t('modal.handlingFee', {
                          percentage: showPercentage(
                            Number(data?.service?.handling_fee)
                          ),
                        })}
                      />
                    </>
                  )}
                  <PaymentRow
                    isTotal
                    currency={currencyData?.name}
                    price={totalPrice}
                    title={t('modal.totalPaid')}
                  />
                  {isFiatCurrency(currencyData?.id) ? (
                    <>
                      <Agreement
                        isChecked={isChecked}
                        setIsChecked={setIsChecked}
                      />
                      {paymentMethod?.payment_gateway?.name ===
                        PaymentGatewayName.CHECKOUT && (
                        <button
                          aria-label={t('modal.mintByVisa')}
                          className="btn btn-primary mt-4"
                          data-test="fiat-minting-btn"
                          disabled={!data}
                          type="button"
                          onClick={() => handleSubmit(handleCheckoutFiatMint)()}
                        >
                          {t('modal.mintByVisa')}
                        </button>
                      )}
                      {paymentMethod?.payment_gateway?.name ===
                        PaymentGatewayName.GKASH && (
                        <GkashPaymentBtn prepayHook={handleGkashFiatMint} />
                      )}
                    </>
                  ) : (
                    <button
                      aria-label={t('modal.mintByCypto')}
                      className="btn btn-primary mt-4"
                      type="button"
                      onClick={() => handleSubmit(handleCryptoMint)()}
                    >
                      {t('modal.mintByCypto')}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default MintModal;
